import { useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { Lock, Pencil, Puzzle, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { HealthDot } from '@/components/extensions/HealthDot'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useAuth } from '@/contexts/AuthContext'
import { EditExtensionDialog, extensionHealthKey } from '@/features/extensions/EditExtensionDialog'
import {
  extensionDescription,
  extensionName,
  extensionNames,
  switchLock,
} from '@/features/extensions/extension-copy'
import { EXTENSIONS_QUERY_KEY } from '@/hooks/use-extensions'
import type { Extension } from '@/services/api/extensions'
import { extensionsApi } from '@/services/api/extensions'

/** Extensions with their own page under /extensions/<id>; the rest live inside the
 * case UI and get no link. OSINT's page holds the register catalog, the local
 * snapshot status/refresh and a search console. */
const EXTENSIONS_WITH_PAGES = new Set(['geocoding', 'osint'])

export default function ExtensionsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isSuperuser = !!user?.is_superuser

  const { data: extensions, isLoading } = useQuery({
    queryKey: EXTENSIONS_QUERY_KEY,
    queryFn: () => extensionsApi.list(),
  })

  const visible = (extensions ?? []).filter(e => e.enabled || e.advertise_when_disabled)

  return (
    <PageShell title={t('extensions.title')} summary={t('extensions.summary')} icon={Puzzle}>
      {!isLoading && visible.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-3">
          {visible.map(ext =>
            ext.enabled ? (
              <EnabledCard
                key={ext.id}
                ext={ext}
                extensions={extensions ?? []}
                isSuperuser={isSuperuser}
              />
            ) : (
              <TeaserCard
                key={ext.id}
                ext={ext}
                extensions={extensions ?? []}
                isSuperuser={isSuperuser}
              />
            )
          )}
        </div>
      )}
    </PageShell>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <Puzzle className="h-10 w-10 text-muted-foreground" />
      <div className="text-base font-medium text-foreground">{t('extensions.empty.title')}</div>
      <p className="max-w-md text-sm text-muted-foreground">{t('extensions.empty.description')}</p>
    </Card>
  )
}

interface CardProps {
  ext: Extension
  /** Every extension, for the names of what this one requires. */
  extensions: readonly Extension[]
  isSuperuser: boolean
}

/** The superuser's pencil and the dialog it opens. */
function EditControl({ ext, extensions }: Omit<CardProps, 'isSuperuser'>) {
  const { t } = useTranslation()
  const [editOpen, setEditOpen] = useState(false)
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditOpen(true)}
        aria-label={t('extensions.edit')}
      >
        <Pencil className="size-4" />
      </Button>
      <EditExtensionDialog
        ext={ext}
        extensions={extensions}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </>
  )
}

function EnabledCard({ ext, extensions, isSuperuser }: CardProps) {
  const { t } = useTranslation()
  const healthQuery = useQuery({
    queryKey: extensionHealthKey(ext.id),
    queryFn: () => extensionsApi.health(ext.id),
    staleTime: 30_000,
  })
  return (
    <Card className="flex flex-wrap items-start gap-x-3 gap-y-2 p-4 md:flex-nowrap md:gap-4 md:p-5">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-muted-foreground">
        <Puzzle className="size-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-y-0.5">
          <h3 className="text-sm font-semibold text-foreground">{extensionName(t, ext)}</h3>
          <HealthDot status={healthQuery.data?.status ?? 'unknown'} />
        </div>
        <p className="text-xs text-muted-foreground">{extensionDescription(t, ext)}</p>
        {ext.url && (
          <p className="font-mono text-xs text-muted-foreground/80 max-md:break-all">{ext.url}</p>
        )}
        {healthQuery.data?.detail && (
          <p className="text-xs text-muted-foreground">{healthQuery.data.detail}</p>
        )}
      </div>
      {/* On a phone the actions drop to their own row under the copy. */}
      <div className="flex shrink-0 items-center gap-2 max-md:w-full max-md:justify-end">
        {isSuperuser && <EditControl ext={ext} extensions={extensions} />}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => healthQuery.refetch()}
          aria-label={t('extensions.refresh')}
        >
          <RefreshCw className={healthQuery.isFetching ? 'size-4 animate-spin' : 'size-4'} />
        </Button>
        {EXTENSIONS_WITH_PAGES.has(ext.id) && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/extensions/${ext.id}`}>{t('extensions.open')} →</Link>
          </Button>
        )}
      </div>
    </Card>
  )
}

/** Why a switched-off extension is off: the deployment, a required extension, or the plan. */
function teaserReason(t: TFunction, ext: Extension, extensions: readonly Extension[]) {
  const missing = ext.missing_requirements ?? []
  const badge = !ext.available
    ? t('extensions.unavailable')
    : missing.length > 0
      ? t('extensions.requires', { names: extensionNames(t, missing, extensions) })
      : t('extensions.notInPlan')
  // The same reason the edit dialog gives under its locked switch.
  return { badge, hint: switchLock(t, ext, extensions) }
}

function TeaserCard({ ext, extensions, isSuperuser }: CardProps) {
  const { t } = useTranslation()
  const reason = teaserReason(t, ext, extensions)
  return (
    <Card className="flex items-start gap-3 p-4 opacity-80 md:gap-4 md:p-5">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-muted-foreground">
        <Lock className="size-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="text-sm font-semibold text-foreground">{extensionName(t, ext)}</h3>
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {reason.badge}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">{extensionDescription(t, ext)}</p>
        {reason.hint && <p className="text-xs text-muted-foreground/80">{reason.hint}</p>}
      </div>
      {isSuperuser && <EditControl ext={ext} extensions={extensions} />}
    </Card>
  )
}
